import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, Sun, Mail, MapPin, Phone, Send, AlertTriangle, Loader2, Facebook } from 'lucide-react';
import { useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import SEO from '../../components/SEO';
import { supabase } from '../../lib/supabase';

export default function Contact() {
    const { t } = useTranslation();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const handleBack = (e: React.MouseEvent) => {
        e.preventDefault();
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const { data, error: functionError } = await supabase.functions.invoke('send-contact-email', {
                body: formData,
                headers: {
                    'Accept-Language': i18n.language,
                },
            });

            if (functionError) throw functionError;

            if (data?.error) {
                const detail = data.details?.message || data.error;
                throw new Error(detail);
            }

            setSubmitted(true);
        } catch (err: any) {
            console.error('Erreur lors de l\'envoi:', err);
            setError(err.message || t('contact.form.error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-dark-bg text-text-main font-sans selection:bg-primary selection:text-white flex flex-col">
            <SEO
                title={t('contact.title')}
                description={t('contact.seo_description')}
                canonical="/legal/contact"
            />
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-bg/80 backdrop-blur-md border-b border-dark-border">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="w-10 h-10 shrink-0">
                            <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                        </Link>
                        <Link to="/" className="text-2xl font-bold text-text-main tracking-tight">CitoyenPro</Link>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                        >
                            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <a
                            href="/"
                            onClick={handleBack}
                            className="flex items-center gap-2 text-sm font-medium text-text-muted hover:text-primary transition-colors"
                        >
                            <ArrowLeft size={16} />
                            {t('contact.nav.back')}
                        </a>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <main className="flex-1 flex items-center justify-center pt-32 pb-16 px-6 max-w-7xl mx-auto w-full">
                <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center w-full max-h-full">
                    <div className="mb-12 md:mb-0">
                        <h1 className="text-4xl lg:text-5xl font-bold text-text-main mb-6">{t('contact.hero.title')}</h1>
                        <p className="text-text-muted text-lg mb-10 leading-relaxed">
                            {t('contact.hero.subtitle')}
                        </p>

                        <div className="space-y-8">
                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                        <Mail size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-text-main text-lg mb-1">{t('contact.info.email.title')}</h3>
                                        <a href="mailto:citoyenpro@nnt-quebec.com" className="text-text-muted hover:text-primary transition-colors block mb-1">citoyenpro@nnt-quebec.com</a>
                                        <p className="text-text-muted text-sm">{t('contact.info.email.response')}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                        <MapPin size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-text-main text-lg mb-1">{t('contact.info.address.title')}</h3>
                                        <p className="text-text-muted">{t('contact.info.address.value')}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                        <Phone size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-text-main text-lg mb-1">{t('contact.info.phone.title')}</h3>
                                        <p className="text-text-muted">+1 (581) 684-8295</p>
                                        <p className="text-xs text-text-muted mt-1">{t('contact.info.phone.hours')}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-8 border-t border-dark-border">
                                <h2 className="text-2xl font-bold text-text-main mb-8">{t('contact.info.others.title')}</h2>

                                <div className="flex items-center gap-4">
                                    <a
                                        href="https://www.facebook.com/CitoyenPro"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Facebook"
                                        className="w-12 h-12 rounded-full bg-dark-bg border border-dark-border flex items-center justify-center text-text-muted hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] transition-all duration-300 shadow-sm group"
                                    >
                                        <Facebook size={24} className="group-hover:scale-110 transition-transform" />
                                    </a>
                                    <a
                                        href="https://www.tiktok.com/@citoyenpro"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="TikTok"
                                        className="w-12 h-12 rounded-full bg-dark-bg border border-dark-border flex items-center justify-center text-text-muted hover:text-white hover:bg-black hover:border-black transition-all duration-300 shadow-sm group"
                                    >
                                        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current group-hover:scale-110 transition-transform">
                                            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.9-.32-1.9-.39-2.81-.12-.52.13-1.03.39-1.46.73-.78.57-1.28 1.51-1.35 2.47-.03.8.31 1.6 1.01 2.01.55.33 1.25.43 1.88.24.93-.2 1.68-.96 1.94-1.87.12-.47.11-.97.11-1.45-.01-4.7-.01-9.4-.01-14.1z" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-dark-card border border-dark-border rounded-3xl p-8 shadow-xl w-full">
                        {!submitted ? (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-text-muted mb-2">{t('contact.form.name.label')}</label>
                                    <input
                                        required
                                        type="text"
                                        className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-text-main"
                                        placeholder={t('contact.form.name.placeholder')}
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-text-muted mb-2">{t('contact.form.email.label')}</label>
                                    <input
                                        required
                                        type="email"
                                        className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-text-main"
                                        placeholder={t('contact.form.email.placeholder')}
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-text-muted mb-2">{t('contact.form.message.label')}</label>
                                    <textarea
                                        required
                                        rows={4}
                                        className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-text-main resize-none"
                                        placeholder={t('contact.form.message.placeholder')}
                                        value={formData.message}
                                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                    />
                                </div>
                                {error && (
                                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                                        <p className="text-sm text-red-500 flex items-center gap-2">
                                            <AlertTriangle size={16} />
                                            {error}
                                        </p>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/25 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="animate-spin" size={20} />
                                            {t('contact.form.sending')}
                                        </>
                                    ) : (
                                        <>
                                            {t('contact.form.submit')}
                                            <Send size={18} />
                                        </>
                                    )}
                                </button>
                            </form>
                        ) : (
                            <div className="text-center py-20">
                                <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center text-green-500 mx-auto mb-6">
                                    <Send size={40} />
                                </div>
                                <h3 className="text-2xl font-bold text-text-main mb-4">{t('contact.success.title')}</h3>
                                <p className="text-text-muted mb-8">
                                    {t('contact.success.desc')}
                                </p>
                                <button
                                    onClick={() => {
                                        setSubmitted(false);
                                        setFormData({ name: '', email: '', message: '' });
                                    }}
                                    className="px-8 py-3 bg-dark-hover text-text-main font-bold rounded-xl border border-dark-border hover:bg-dark-bg transition-all"
                                >
                                    {t('contact.success.cta')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-dark-card border-t border-dark-border py-4 text-center shrink-0">
                <p className="text-text-muted text-sm">{t('contact.footer.copyright')}</p>
            </footer>
        </div>
    );
}
